"use client";

import { useId, useState } from "react";
import { useRouter } from "next/navigation";
import { Bell, CheckCheck, Loader2 } from "lucide-react";
import { useIconRailClose } from "@workspace/gamja-platform/components/icon-rail";
import { navBadgeClassNames } from "@/components/nav-badges";
import { Button, buttonVariants } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  useMarkNotificationsRead,
  useNotificationList,
  useUnreadNotificationCount,
} from "@/hooks/useNotifications";
import { parseCommentTargetId } from "@/lib/comment-links";
import { requestCommentReveal } from "@/lib/comment-reveal";
import { resolveNotificationNavigation } from "@/lib/notifications/navigation";
import {
  formatRelativeTime,
  formatUnreadBadge,
  presentNotification,
} from "@/lib/notifications/present";
import type { AnyNotificationItem } from "@/types/notification";

/** 팝오버 안 버튼 공통 포커스 링 — 레일 아이콘과 같은 amber (three-column-layout.md 레일 a11y) */
const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500";

/**
 * 알림 벨 (JCG-3601) — 미읽음 뱃지 + 팝오버 목록.
 *
 * - `rail`: 데스크탑 아이콘 레일(공용 IconRail 의 `notifications` 슬롯). 레일 아이콘과 같은 42px 정사각 버튼, 팝오버는 오른쪽.
 * - `header`: 상단 NAV(클래식 데스크탑·모바일)에서 프로필 버튼 왼쪽. 공용 `Button` ghost/icon 과 같은 모양, 팝오버는 아래.
 * 비로그인·Clerk 미로드 상태에서는 아무것도 렌더하지 않는다(알림이 없는 앱에서 죽은 UI 가 뜨지 않게 하는 슬롯 규칙과 동일).
 *
 * 팝오버는 body 포털이라 레일 DOM 밖에 뜬다. 공용 IconRail 의 외부 클릭 닫기가 드로어(와 그 안의 벨)를 언마운트하지
 * 않도록 콘텐츠 루트에 `data-rail-floating-ui` 를 달고, 항목을 눌러 이동할 때는 `useIconRailClose()` 로 드로어를 직접
 * 닫는다(드로어 링크의 `onClick={closeAll}` 과 같은 계약). 헤더 variant 에서는 둘 다 no-op 이다.
 *
 * 항목 클릭: 미읽음이면 읽음 처리 후 `link` 로 이동한다. 같은 문서 안의 해시 이동은 `location.hash` 를 직접 바꿔
 * 댓글 딥링크 훅(`useCommentDeepLink`)이 `hashchange` 로 재실행되게 하고, 이미 같은 해시면 `requestCommentReveal` 로
 * 다시 보여 달라고 요청한다(`resolveNotificationNavigation`). 뱃지 색은 `nav-badges` 의 `Now`(red) 클래스를 그대로 쓴다.
 */
export default function NotificationBell({
  variant = "header",
  className,
}: {
  variant?: "rail" | "header";
  className?: string;
}) {
  const { enabled, unreadCount } = useUnreadNotificationCount();
  const [open, setOpen] = useState(false);
  const { items, isPending, isError, refetch } = useNotificationList(open);
  const markRead = useMarkNotificationsRead();
  const router = useRouter();
  const closeRail = useIconRailClose();
  const headingId = useId();

  if (!enabled) return null;

  const badge = formatUnreadBadge(unreadCount);
  const ariaLabel =
    unreadCount > 0 ? `알림 — 안 읽은 알림 ${unreadCount}개` : "알림";

  const handleSelect = (item: AnyNotificationItem) => {
    if (!item.readAt) markRead.mutate([item.id]);
    setOpen(false);
    closeRail();
    const navigation = resolveNotificationNavigation(
      item.link,
      window.location.pathname + window.location.search,
    );
    if (navigation.kind === "push") {
      router.push(navigation.href);
      return;
    }
    if (window.location.hash !== navigation.hash) {
      window.location.hash = navigation.hash;
      return;
    }
    // 같은 해시를 다시 대입하면 hashchange 가 나지 않는다 — 딥링크 훅에 명시적으로 다시 보여 달라고 요청한다
    const commentId = parseCommentTargetId(navigation.hash);
    if (commentId) requestCommentReveal(commentId);
  };

  const triggerClassName =
    variant === "rail"
      ? cn(
          "flex h-[42px] w-[42px] items-center justify-center rounded-xl text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
          FOCUS_RING,
          // 열림 상태는 드로어 섹션 행의 expanded 와 같은 신호(bg-card + shadow)
          open && "bg-card text-foreground shadow-sm",
        )
      : buttonVariants({ variant: "ghost", size: "icon" });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="알림"
          aria-label={ariaLabel}
          className={cn("relative", triggerClassName, className)}
        >
          {/* 헤더 variant 의 아이콘 크기는 공용 Button 이 svg 를 size-6 으로 강제하므로 형제(테마 토글·계정)와 같다 */}
          <Bell aria-hidden="true" className={variant === "rail" ? "h-[18px] w-[18px]" : undefined} />
          <UnreadBadge label={badge} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-rail-floating-ui=""
        aria-labelledby={headingId}
        side={variant === "rail" ? "right" : "bottom"}
        align="end"
        sideOffset={8}
        // z-[60]: 레일 드로어 포털(z-50)이 팝오버보다 나중에 body 에 붙어도 팝오버가 위에 남게 한다
        className="z-[60] w-80 max-w-[calc(100vw-2rem)] p-0"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
          <h2 id={headingId} className="text-sm font-bold text-foreground">
            알림
            {unreadCount > 0 && (
              <span className="ml-1.5 text-xs font-semibold text-amber-600 dark:text-amber-400">
                {unreadCount}
              </span>
            )}
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => markRead.mutate(undefined)}
            disabled={unreadCount === 0 || markRead.isPending}
            className={cn("h-auto px-1.5 py-1 text-xs text-muted-foreground hover:text-foreground", FOCUS_RING)}
          >
            <CheckCheck className="size-3.5" aria-hidden="true" />
            모두 읽음
          </Button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto">
          {isPending ? (
            <div className="flex items-center justify-center py-8" role="status" aria-live="polite">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" aria-hidden="true" />
              <span className="sr-only">알림을 불러오는 중</span>
            </div>
          ) : isError ? (
            <div className="px-3 py-8 text-center text-sm text-muted-foreground" role="alert">
              알림을 불러올 수 없어요.
              <Button
                type="button"
                variant="link"
                onClick={() => void refetch()}
                className={cn("ml-1 h-auto p-0 text-sm text-amber-600 dark:text-amber-400", FOCUS_RING)}
              >
                다시 시도
              </Button>
            </div>
          ) : items.length === 0 ? (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">
              새 알림이 없어요.
            </p>
          ) : (
            <ul aria-label="알림 목록">
              {items.map((item) => (
                <NotificationRow key={item.id} item={item} onSelect={handleSelect} />
              ))}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function UnreadBadge({ label }: { label: string | null }) {
  if (!label) return null;
  return (
    <span
      aria-hidden="true"
      className={cn(
        // nav-badges 의 `Now`(red) 와 같은 색 — 다크모드 대비 규칙을 한 곳에서 유지한다
        navBadgeClassNames.Now,
        "absolute -right-0.5 -top-0.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none",
      )}
    >
      {label}
    </span>
  );
}

function NotificationRow({
  item,
  onSelect,
}: {
  item: AnyNotificationItem;
  onSelect: (item: AnyNotificationItem) => void;
}) {
  const { title, description } = presentNotification(item);
  const unread = item.readAt === null;
  return (
    <li className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => onSelect(item)}
        className={cn(
          "flex w-full items-start gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-accent focus-visible:ring-inset",
          FOCUS_RING,
          unread && "bg-amber-50/70 dark:bg-amber-950/30",
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            "mt-1.5 h-2 w-2 shrink-0 rounded-full",
            unread ? "bg-amber-500 dark:bg-amber-400" : "bg-transparent",
          )}
        />
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              "block text-sm leading-snug text-foreground line-clamp-2",
              unread ? "font-semibold" : "font-medium",
            )}
          >
            {title}
          </span>
          {description && (
            <span className="mt-0.5 block text-xs leading-snug text-muted-foreground line-clamp-2">
              {description}
            </span>
          )}
          <time
            dateTime={item.createdAt}
            className="mt-1 block text-[11px] text-muted-foreground tabular-nums"
          >
            {formatRelativeTime(item.createdAt)}
            {unread && <span className="sr-only"> · 안 읽음</span>}
          </time>
        </span>
      </button>
    </li>
  );
}
